import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { FIELD_LENGTH, InputText } from '@sm360/widget-commons-forms';
import { AccountService } from '@sm360/widget-commons-general';
import { noop, isFunction, debounce } from 'lodash';
import CurrencyFormats from 'commons-vehicle/constants/currencyFormats.js';
import TradeInVehicleModel from 'commons-vehicle/Vehicle/models/TradeInVehicleModel.js';
import TradeInVehicleAPIService from 'commons-vehicle/Vehicle/services/TradeInVehicleAPIService/TradeInVehicleAPIService.js';

import LOCAL_EVENTS from 'commons-vehicle/constants/events.js';

import 'commons-vehicle/Vehicle/components/PaymentOptionFilters/TradeInValue/TradeInValue.scss';

class TradeInValue extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            inputs: {
                tradeIn: 0,
                lien: 0,
            },
            formValidation: {
                tradeIn: false,
                lien: false,
            },
            hasTradeIn: true,
        };

        this.debounceTradeIn = debounce(this.props.setTradeIn, 300);
        this.debounceLien = debounce(this.props.setLien, 300);

        this.handleInputChange = this.handleInputChange.bind(this);
        this.ignoreInput = this.ignoreInput.bind(this);
        this.saveTradeInVehicle = this.saveTradeInVehicle.bind(this);
        this.setLien = this.setLien.bind(this);
        this.setTradeIn = this.setTradeIn.bind(this);
        this.onAuthentication = this.onAuthentication.bind(this);
        this.updateTradeInStateValues = this.updateTradeInStateValues.bind(this);
        this.fetchTradeInValuesFromLocalStorage = this.fetchTradeInValuesFromLocalStorage.bind(this);
        this.renderCreditTaxes = this.renderCreditTaxes.bind(this);
    }

    componentDidMount() {
        this.fetchTradeInValuesFromAPI()
            .catch(this.fetchTradeInValuesFromLocalStorage)
            .then(this.updateTradeInStateValues);

        const $window = WindowProxy.get();
        if ($window) {
            $window.addEventListener(LOCAL_EVENTS.AUTHENTICATED, this.onAuthentication);
        }
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.removeEventListener(LOCAL_EVENTS.AUTHENTICATED, this.onAuthentication);
        }
    }

    onAuthentication() {
        return this.fetchTradeInValuesFromAPI()
            .catch(this.fetchTradeInValuesFromLocalStorage)
            .then(this.updateTradeInStateValues);
    }

    setTradeIn(value) {
        this.debounceTradeIn(value);
        if (value <= 0) {
            this.setLien(0);
        }
    }

    setLien(value) {
        this.debounceLien(value);
    }

    fetchTradeInValuesFromLocalStorage() {
        const windowProxy = WindowProxy.get();
        if (!windowProxy || !windowProxy.localStorage) {
            return Promise.resolve({
                tradeIn: 0,
                lien: 0,
            });
        }

        const { tradeIn, lien } = windowProxy.localStorage;

        return Promise.resolve({
            tradeIn: Number.parseInt(tradeIn, 10) || 0,
            lien: Number.parseInt(lien, 10) || 0,
        });
    }

    fetchTradeInValuesFromAPI() {
        const isAuthenticated = AccountService.isAuthenticated();
        if (!isAuthenticated) {
            return Promise.reject();
        }

        return TradeInVehicleAPIService.loadTradeInVehicle().then(({ amount, lien }) => {
            if (!amount) {
                return Promise.reject();
            }
            return {
                lien,
                tradeIn: amount,
            };
        });
    }

    updateTradeInStateValues(values) {
        this.setState({ inputs: values });
        this.props.setTradeIn(values.tradeIn);
        this.props.setLien(values.lien);
    }

    saveTradeInVehicle() {
        if (!isFunction(this.props.tradeInVehicle.checkIsUpdatable) || this.props.tradeInVehicle.checkIsUpdatable()) {
            this.props.saveTradeInVehicle(this.props.tradeInVehicle);
        }
    }

    ignoreInput() {
        if (this.state.hasTradeIn) {
            this.setTradeIn(0);
        } else {
            this.setTradeIn(this.state.inputs.tradeIn);
            if (this.state.inputs.tradeIn > 0) {
                this.setLien(this.state.inputs.lien);
            }
        }
        this.setState({
            hasTradeIn: !this.state.hasTradeIn,
        });
    }

    handleInputChange(value, name) {
        let parseValue = parseInt(value, 10);
        parseValue = !Number.isNaN(parseValue) ? parseValue : 0;

        const { formValidation, inputs } = this.state;

        if (inputs[name] !== value) {
            inputs[name] = value;
            formValidation[name] = true;

            if (name === 'tradeIn') {
                if (parseValue <= 0) {
                    inputs.lien = 0;
                }
                this.setTradeIn(parseValue);
            }
            if (name === 'lien') {
                this.setLien(inputs.tradeIn > 0 ? parseValue : 0);
            }
            if (this.props.initialTargetPayment > 0) {
                this.props.setTargetPayment(this.props.initialTargetPayment);
            }
        }
        this.setState({
            inputs,
            formValidation,
        });
    }

    renderCreditTaxes() {
        return (
            <small>
                {LocaleService.t('PAYMENT_OPTIONS_FILTERS.TRADE_IN_TAXES_CREDIT_DETAILS', {
                    tradeInWithTaxCreditsAmount: LocaleService.l(
                        parseInt(this.state.inputs.tradeIn, 10) + this.props.tradeInVehicle.getTaxCreditsV25() || 0,
                        CurrencyFormats.CURRENCY_2
                    ),
                    taxCreditsAmount: LocaleService.l(
                        this.props.tradeInVehicle.getTaxCreditsV25() || 0,
                        CurrencyFormats.CURRENCY_2
                    ),
                })}
            </small>
        );
    }

    render() {
        const tradeInVehicleIsLocked =
            isFunction(this.props.tradeInVehicle.checkIsLocked) && this.props.tradeInVehicle.checkIsLocked();
        const hasTradeInValue = !!Number.parseInt(this.state.inputs.tradeIn, 10);
        const isLienAvailableClass = ['trade-in__input'];
        const isTradeinAvailable = ['trade-in__input'];
        let isDisabled = false;

        if (this.state.inputs.tradeIn <= 0 || !this.state.hasTradeIn || tradeInVehicleIsLocked) {
            isLienAvailableClass.push('forms_disabled');
            isDisabled = true;
        }
        if (!this.state.hasTradeIn || tradeInVehicleIsLocked) {
            isTradeinAvailable.push('forms_disabled');
        }
        /* TODO: Put this back
        const isBtnToggle = !this.state.hasTradeIn ? (
            'disabled-btn btn-toggle-input'
        ) : (
        '   btn-toggle-input'
        ); */

        return (
            <div className="listing-used-payment-wrap-range">
                <div
                    className={isTradeinAvailable.join(' ')}
                    role="button"
                    tabIndex={0}
                    onClick={this.props.onValueClick}
                >
                    <InputText
                        type="number"
                        name="tradeIn"
                        classNames={['styled-placeholder', 'half', 'currency']}
                        label={LocaleService.t('FILTERS.ESTIMATED_EXCHANGE')}
                        value={this.state.inputs.tradeIn !== 0 ? this.state.inputs.tradeIn : ''}
                        isValid={this.state.formValidation.tradeIn}
                        valueChange={this.handleInputChange}
                        onBlur={this.saveTradeInVehicle}
                        maxLength={FIELD_LENGTH.DEFAULT_NUMBER_MAX}
                    />
                </div>
                <div
                    className={isLienAvailableClass.join(' ')}
                    role="button"
                    tabIndex={0}
                    onClick={this.props.onLienClick}
                >
                    <InputText
                        type="number"
                        name="lien"
                        classNames={['styled-placeholder', 'half', 'currency']}
                        label={LocaleService.t('FILTERS.ESTIMATED_DEBTS')}
                        value={this.state.inputs.lien !== 0 ? this.state.inputs.lien : ''}
                        isValid={this.state.formValidation.lien}
                        valueChange={this.handleInputChange}
                        onBlur={this.saveTradeInVehicle}
                        isDisabled={isDisabled}
                        maxLength={FIELD_LENGTH.DEFAULT_NUMBER_MAX}
                    />
                </div>
                <div>{hasTradeInValue && this.renderCreditTaxes()}</div>
                {/* <div className={isBtnToggle}>
                    <p
                        className="listing-used-ignore-tradein"
                        onClick={this.ignoreInput}
                    >
                        {LocaleService.t('FILTERS.IGNORE_VALUE')}
                    </p>
                </div> */}
            </div>
        );
    }
}

TradeInValue.defaultProps = {
    setTargetPayment: noop,
    tradeInVehicle: new TradeInVehicleModel(),
    onValueClick: noop,
    onLienClick: noop,
};

TradeInValue.propTypes = {
    initialTargetPayment: PropTypes.number.isRequired,
    saveTradeInVehicle: PropTypes.func.isRequired,
    setLien: PropTypes.func.isRequired,
    setTargetPayment: PropTypes.func,
    setTradeIn: PropTypes.func.isRequired,
    tradeInVehicle: PropTypes.instanceOf(TradeInVehicleModel),
    onValueClick: PropTypes.func,
    onLienClick: PropTypes.func,
};

TradeInValue.displayName = 'TradeInValue';

export default TradeInValue;
